import React, { useState, useEffect, useRef } from "react";
import "./PremiumStyles.css";

interface EnhancedTokenCardProps {
  token: {
    id: string;
    name: string;
    symbol: string;
    description?: string;
    image: string;
    bondingCurveId?: string; // Add bonding curve ID to token interface
  };
  onClick: () => void;
}

// Hook for fetching bonding curve data
const useBondingCurveData = (tokenId: string | null) => {
  const [bondingCurveData, setBondingCurveData] = useState<{
    progressPercentage: number;
    currentSuiBalance: number;
    totalSuiNeeded: number;
    suiNeededForPool: number;
    totalMinted: number;
    totalMintedNormalized: number;
    poolCreated: boolean;
    salesSummary: {
      buyTrades: number;
      sellTrades: number;
      tokensSold: number;
      tokensBoughtBack: number;
      suiCollected: number;
      suiSpent: number;
      progressFromSales: number;
      suiNeededFromSales: number;
    } | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstFetchRef = useRef(true);

  useEffect(() => {
    if (!tokenId) {
      setBondingCurveData(null);
      return;
    }

    const fetchBondingCurveData = async () => {
      if (firstFetchRef.current) {
        setLoading(true);
      }
      setError(null);

      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin + "/api";
        const res = await fetch(`${BACKEND_URL}/tokens/${tokenId}/bonding-curve`);
        if (!res.ok) {
          throw new Error(`Failed to fetch bonding curve data: ${res.statusText}`);
        }

        const data = await res.json();

        if (data.success) {
          const defaultSales = {
            buyTrades: 0,
            sellTrades: 0,
            tokensSold: 0,
            tokensBoughtBack: 0,
            suiCollected: 0,
            suiSpent: 0,
            progressFromSales: 0,
            suiNeededFromSales: 0,
          };

          const parsed = {
            progressPercentage: Number(data.progressPercentage) || 0,
            currentSuiBalance: Number(data.currentSuiBalance) || 0,
            totalSuiNeeded: Number(data.totalSuiNeeded) || 0,
            suiNeededForPool: Number(data.suiNeededForPool) || 0,
            totalMinted: Number(data.totalMinted) || 0,
            totalMintedNormalized: Number(data.totalMintedNormalized) || 0,
            poolCreated: Boolean(data.poolCreated),
            salesSummary: data.salesSummary ? {
              buyTrades: Number(data.salesSummary.buyTrades) || 0,
              sellTrades: Number(data.salesSummary.sellTrades) || 0,
              tokensSold: Number(data.salesSummary.tokensSold) || 0,
              tokensBoughtBack: Number(data.salesSummary.tokensBoughtBack) || 0,
              suiCollected: Number(data.salesSummary.suiCollected) || 0,
              suiSpent: Number(data.salesSummary.suiSpent) || 0,
              progressFromSales: Number(data.salesSummary.progressFromSales) || 0,
              suiNeededFromSales: Number(data.salesSummary.suiNeededFromSales) || 0,
            } : defaultSales,
          };

          setBondingCurveData((prev) => {
            if (
              prev &&
              prev.progressPercentage === parsed.progressPercentage &&
              prev.currentSuiBalance === parsed.currentSuiBalance &&
              prev.totalSuiNeeded === parsed.totalSuiNeeded &&
              prev.suiNeededForPool === parsed.suiNeededForPool &&
              prev.totalMinted === parsed.totalMinted &&
              prev.totalMintedNormalized === parsed.totalMintedNormalized &&
              prev.poolCreated === parsed.poolCreated &&
              JSON.stringify(prev.salesSummary) === JSON.stringify(parsed.salesSummary)
            ) {
              return prev;
            }
            return parsed;
          });
        } else {
          setError(data.error || 'Failed to fetch bonding curve data');
        }
      } catch (err: any) {
        setError(err.message || 'Error fetching bonding curve data');
        console.error('Error fetching bonding curve data:', err);
      } finally {
        if (firstFetchRef.current) {
          setLoading(false);
          firstFetchRef.current = false;
        }
      }
    };

    fetchBondingCurveData();

    const interval = setInterval(fetchBondingCurveData, 30000);

    return () => clearInterval(interval);
  }, [tokenId]);

  return { bondingCurveData, loading, error };
};

const MILESTONES = [20, 40, 60, 80, 100];

const EnhancedTokenCard: React.FC<EnhancedTokenCardProps> = ({
  token,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [glowIntensity, setGlowIntensity] = useState(0.4);
  const cardRef = useRef<HTMLDivElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [firedMilestones, setFiredMilestones] = useState<number[]>([]);
  const [fireworks, setFireworks] = useState<{ id: number; milestone: number }[]>([]);
  const [imageError, setImageError] = useState(false);

  // Use real bonding curve data
  const { bondingCurveData, loading } = useBondingCurveData(token.id);
  const curveProgress = bondingCurveData?.progressPercentage || 0;
  const clampedProgress = Math.max(0, Math.min(100, curveProgress));
  const displayProgress = Math.max(0, Math.min(100, Math.round(clampedProgress)));
  const currentSui = bondingCurveData?.currentSuiBalance || 0;
  const totalSuiNeeded = bondingCurveData?.totalSuiNeeded || 0;
  const suiNeededForPool = bondingCurveData?.suiNeededForPool || Math.max(totalSuiNeeded - currentSui, 0);
  const formatSui = (value: number) => `${(value / 1_000_000_000).toFixed(3)} SUI`;
  const currentSuiFormatted = formatSui(currentSui);
  const targetSuiFormatted = formatSui(totalSuiNeeded);
  const remainingSuiFormatted = formatSui(suiNeededForPool);
  const isPublished = Boolean(bondingCurveData?.poolCreated && curveProgress >= 100);
  const sales = bondingCurveData?.salesSummary;
  const tokensSoldDisplay = sales ? sales.tokensSold : 0;
  const collectedSuiDisplay = sales ? sales.suiCollected : 0;

  useEffect(() => {
    if (loading) return;
    const roundedProgress = Math.round(clampedProgress);
    const newHits = MILESTONES.filter(
      (threshold) => roundedProgress >= threshold && !firedMilestones.includes(threshold)
    );

    if (newHits.length > 0) {
      setFiredMilestones((prev) => [...prev, ...newHits]);
      setFireworks((prev) => [
        ...prev,
        ...newHits.map((milestone) => ({ id: Date.now() + milestone, milestone }))
      ]);
    }
  }, [clampedProgress, firedMilestones, loading]);

  useEffect(() => {
    if (fireworks.length === 0) return;
    const timers = fireworks.map((fw) =>
      window.setTimeout(() => {
        setFireworks((prev) => prev.filter((item) => item.id !== fw.id));
      }, 2000)
    );
    return () => timers.forEach(clearTimeout);
  }, [fireworks]);

  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasLoaded(true);
    }, Math.random() * 300 + 100);
    return () => clearTimeout(timer);
  }, []);

  // Pulse glow effect (keep for visual appeal)
  useEffect(() => {
    const interval = setInterval(() => {
      setGlowIntensity((prev) => {
        const newValue = prev + (Math.random() * 0.1 - 0.05);
        return Math.max(0.3, Math.min(0.6, newValue)); // Keep between 0.3 and 0.6
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  // Rotate glow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setRotationDeg((prev) => (prev + 1) % 360);
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Handle mouse move effect for 3D card
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!cardRef.current) return;

      const card = cardRef.current;
      const rect = card.getBoundingClientRect();

      // Calculate mouse position relative to card center
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate distance from center (normalize from -1 to 1)
      const distanceX = (e.clientX - centerX) / (rect.width / 2);
      const distanceY = (e.clientY - centerY) / (rect.height / 2);

      // Apply rotation effect (max 4 degrees)
      const rotateY = distanceX * 4;
      const rotateX = -distanceY * 4;

      // Apply transform
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;

      // Create lighting effect
      const light = card.querySelector(".card-lighting") as HTMLElement;
      if (light) {
        light.style.background = `radial-gradient(circle at ${e.clientX - rect.left
          }px ${e.clientY - rect.top
          }px, rgba(255,255,255,0.15), transparent 80%)`;
      }
    };

    const handleMouseLeave = () => {
      if (!cardRef.current) return;
      const card = cardRef.current;
      card.style.transform =
        "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)";

      const light = card.querySelector(".card-lighting") as HTMLElement;
      if (light) {
        light.style.background = "transparent";
      }
    };

    const card = cardRef.current;
    if (card) {
      card.addEventListener("mousemove", handleMouseMove);
      card.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (card) {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={`premium-coin-card ${isPublished ? "published" : ""}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        opacity: hasLoaded ? 1 : 0,
        transform: hasLoaded ? "translateY(0)" : "translateY(10px)",
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Lighting effect overlay */}
      <div className="card-lighting"></div>

      {/* Glow effects */}
      <div
        className="glow-effect glow-effect-primary"
        style={{
          width: "80px",
          height: "80px",
          top: "10%",
          left: "10%",
          opacity: glowIntensity * 0.5,
        }}
      />
      <div
        className="glow-effect glow-effect-secondary"
        style={{
          width: "60px",
          height: "60px",
          bottom: "20%",
          right: "15%",
          opacity: glowIntensity * 0.3,
        }}
      />

      {/* Coin image with animated glow */}
      <div className="premium-coin-image-container">
        <div
          className="premium-coin-glow-ring"
          style={{
            transform: `rotate(${rotationDeg}deg)`,
            opacity: isHovered ? 1 : 0.7,
          }}
        >
          <div className="premium-coin-image-wrapper">
            {token.image && !imageError ? (
              <img
                src={token.image}
                alt={token.name}
                className="premium-coin-image"
                onError={() => setImageError(true)}
                style={{
                  transform: isHovered ? "scale(1.05)" : "scale(1)",
                  boxShadow: isHovered
                    ? `0 0 20px rgba(16, 185, 129, ${glowIntensity + 0.1})`
                    : `0 0 10px rgba(16, 185, 129, ${glowIntensity})`,
                }}
              />
            ) : (
              <div className="premium-coin-placeholder">
                {token.symbol.substring(0, 2)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Coin information */}
      <div className="premium-coin-info">
        <h3 className="premium-coin-name">{token.name}</h3>
        <div className="premium-coin-symbol">${token.symbol}</div>

        {token.description && (
          <div className="premium-coin-description">
            {token.description.length > 60
              ? `${token.description.substring(0, 60)}...`
              : token.description}
          </div>
        )}

        {isPublished ? (
          <div className="premium-published-section">
            <div className="premium-published-badge">Premium Level</div>
            <p className="premium-published-copy">
              Liquidity has moved on-chain. Trade this token directly on Suivision for the best depth.
            </p>
            <a
              className="premium-published-link"
              href="http://suivision.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Open Suivision DEX ↗
            </a>
          </div>
        ) : (
          <>
            <div className="premium-coin-stats">
              <div className="premium-coin-stat">
                <div className="premium-coin-stat-label">Curve Status</div>
                <div className="premium-coin-stat-value" style={{ color: "#10b981" }}>Active</div>
              </div>
              <div className="premium-coin-stat">
                <div className="premium-coin-stat-label">Progress</div>
                <div className="premium-coin-stat-value" style={{ color: "#10b981" }}>
                  {loading ? '...' : displayProgress + '%'}
                </div>
              </div>
            </div>

            <div className="premium-progress-section">
              <div className="premium-progress-bar">
                <div className="premium-progress-track"></div>
                <div
                  className="premium-progress-fill"
                  style={{ width: `${loading ? 0 : displayProgress}%` }}
                ></div>
                <div className="premium-progress-highlight" style={{ left: `${loading ? 0 : displayProgress}%` }}></div>
                <div className="premium-progress-overlay"></div>
              </div>
              <div className="premium-progress-meta">
                <div className="premium-progress-meta-item">
                  <span className="premium-meta-label">Raised</span>
                  <span className="premium-meta-value">{loading ? '...' : currentSuiFormatted}</span>
                </div>
                <div className="premium-progress-meta-item">
                  <span className="premium-meta-label">Goal</span>
                  <span className="premium-meta-value">{loading ? '...' : targetSuiFormatted}</span>
                </div>
                <div className="premium-progress-meta-item">
                  <span className="premium-meta-label">To Pool</span>
                  <span className="premium-meta-value">{loading ? '...' : remainingSuiFormatted}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {fireworks.length > 0 && (
        <div className="premium-fireworks-layer">
          {fireworks.map((fw) => (
            <div key={fw.id} className={`firework firework-${fw.milestone}`} />
          ))}
        </div>
      )}

      <style>{`
        .card-lighting {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
          border-radius: 20px;
        }

        .premium-coin-image-container {
          position: relative;
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .premium-coin-glow-ring {
          position: relative;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: conic-gradient(from 0deg, #10b981, #34d399, #6ee7b7, #10b981);
          padding: 3px;
          filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.4));
          transition: all 0.5s ease;
        }

        .premium-coin-image-wrapper {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: linear-gradient(135deg, #1e293b, #0f172a);
          overflow: hidden;
        }

        .premium-coin-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.5s ease;
        }

        .premium-coin-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          color: #10b981;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), transparent);
        }

        .premium-coin-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .premium-coin-name {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(90deg, #e2e8f0, #10b981, #e2e8f0);
          background-size: 200% auto;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 6s linear infinite;
        }

        .premium-coin-symbol {
          font-size: 16px;
          color: #10b981;
          font-weight: 600;
          text-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
        }

        .premium-coin-description {
          font-size: 14px;
          color: #94a3b8;
          margin-top: 4px;
          line-height: 1.4;
        }

        .premium-coin-stats {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin: 12px 0 8px;
        }

        .premium-coin-stat {
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.7), rgba(30, 41, 59, 0.4));
          border: 1px solid rgba(45, 212, 191, 0.24);
          border-radius: 14px;
          padding: 10px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 52px;
          position: relative;
          overflow: hidden;
        }

        .premium-coin-stat::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(circle at top right, rgba(94, 234, 212, 0.18), transparent 60%);
          opacity: 0.7;
        }

        .premium-coin-stat-label {
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.7px;
          margin-bottom: 4px;
        }

        .premium-coin-stat-value {
          font-size: 15px;
          font-weight: 600;
          color: #e2e8f0;
          position: relative;
          z-index: 1;
        }

        .premium-mini-curve {
          position: relative;
          width: 100%;
          height: 24px;
          margin-top: 8px;
        }

        .premium-mini-curve-bg {
          position: absolute;
          width: 100%;
          height: 4px;
          background: rgba(15, 23, 42, 0.5);
          border-radius: 2px;
          top: 50%;
          transform: translateY(-50%);
        }

        .premium-mini-curve-line {
          position: absolute;
          width: 100%;
          height: 4px;
          background: linear-gradient(to right, #10b981, #34d399);
          border-radius: 2px;
          top: 50%;
          transform: translateY(-50%);
          opacity: 0.5;
        }

        .premium-mini-curve-progress {
          position: absolute;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #10b981;
          top: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 0 10px rgba(16, 185, 129, 0.6);
        }

        .premium-mini-curve-progress::after {
          content: '';
          position: absolute;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 1px solid rgba(16, 185, 129, 0.3);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pulse 2s infinite;
        }

        .premium-progress-section {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 12px;
        }

        .premium-progress-bar {
          position: relative;
          height: 16px;
          border-radius: 12px;
          background: rgba(15, 23, 42, 0.75);
          overflow: hidden;
          border: 1px solid rgba(94, 234, 212, 0.25);
          box-shadow:
            inset 0 0 10px rgba(15, 118, 110, 0.35),
            0 10px 24px rgba(13, 148, 136, 0.15);
        }

        .premium-progress-track {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(15, 23, 42, 0.9), rgba(30, 64, 175, 0.1));
        }

        .premium-progress-fill {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          border-radius: 12px;
          background: linear-gradient(90deg, #14f1a6, #34d399, #059669);
          box-shadow: 0 0 22px rgba(20, 241, 166, 0.55);
          transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .premium-progress-highlight {
          position: absolute;
          top: 2px;
          width: 28px;
          height: 12px;
          border-radius: 12px;
          background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.85), rgba(20, 241, 166, 0));
          transform: translateX(-50%);
          transition: left 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
          opacity: 0.9;
        }

        .premium-progress-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0)),
            radial-gradient(circle at 10% 50%, rgba(94, 234, 212, 0.25), transparent 55%),
            radial-gradient(circle at 90% 50%, rgba(34, 197, 94, 0.2), transparent 55%);
          mix-blend-mode: screen;
          opacity: 0.8;
        }

        .premium-progress-meta {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 4px;
          margin-top: 10px;
          padding: 8px 4px;
          background: rgba(15, 23, 42, 0.4);
          border-radius: 12px;
          border: 1px solid rgba(16, 185, 129, 0.1);
        }

        .premium-progress-meta-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          border-right: 1px solid rgba(148, 163, 184, 0.1);
        }

        .premium-progress-meta-item:last-child {
          border-right: none;
        }

        .premium-meta-label {
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          font-weight: 600;
        }

        .premium-meta-value {
          font-size: 13px;
          font-weight: 700;
          color: #10b981;
          white-space: nowrap;
        }

        .premium-fireworks-layer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          display: flex;
          justify-content: center;
          align-items: center;
          mix-blend-mode: screen;
          overflow: hidden;
        }

        .firework {
          position: absolute;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background:
            radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(56, 189, 248, 0.2) 40%, rgba(15, 23, 42, 0) 70%),
            radial-gradient(circle, rgba(20, 241, 166, 0.6) 0%, rgba(15, 23, 42, 0) 80%);
          animation: firework-burst 1.8s ease-out forwards;
          opacity: 0.85;
        }

        .firework-100 {
          background:
            radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(252, 211, 77, 0.4) 35%, rgba(15, 23, 42, 0) 70%),
            radial-gradient(circle, rgba(249, 115, 22, 0.6) 0%, rgba(15, 23, 42, 0) 80%);
        }

        .firework::after,
        .firework::before {
          content: '';
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          border: 2px solid rgba(148, 163, 184, 0.35);
          animation: firework-ring 1.8s ease-out forwards;
        }

        @keyframes firework-burst {
          0% {
            transform: scale(0.25);
            opacity: 0;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }

        @keyframes firework-ring {
          0% {
            transform: scale(0.2);
            opacity: 0;
          }
          60% {
            opacity: 0.6;
          }
          100% {
            transform: scale(1.6);
            opacity: 0;
          }
        }

        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
        }

        @keyframes shimmer {
          to {
            background-position: 200% center;
          }
        }
      `}</style>
    </div>
  );
};

export default EnhancedTokenCard;
